import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMemo } from "react";

import { isValidGroupedChartQuery } from "../../utils/DashboardUtils";
import useGroupedChartData from "./WidgetHooks/useGroupedChartData";
import GroupedChartTooltip from "./Tooltips/GroupedChartTooltip";
import { BaseChartProps } from "./WidgetTypes";
import useThemeColors from "./useThemeColors";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

// https:bizcharts.taobao.com/product/BizCharts4/demo/352
const GroupedLineChartWidget = ({
  section,
  setSettings,
  theme,
  topLevelFilters,
  viewFilters,
  record,
}: BaseChartProps) => {
  const {
    data,
    keys,
    error,
    loading,
    xAxisField,
    xAxisFieldName,
    groupByField,
    groupByFieldName,
    groupByFieldType,
  } = useGroupedChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    validator: isValidGroupedChartQuery,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    record: record,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData: data as any });

  const MyChart = useMemo(() => {
    if (loading || !data) return null;

    const uniqueKeys = data.reduce((keys, obj) => {
      Object.keys(obj).forEach((key) => {
        if (key !== xAxisField) {
          keys.add(key);
        }
      });
      return keys;
    }, new Set());

    const uniqueKeysArray = [...uniqueKeys];

    const finalData = data.map((d) => {
      for (const key of uniqueKeysArray) {
        if (d[key] === undefined || d[key] === null) {
          if (groupByFieldType === "Tags") {
            const tag = data.find((d) => !!d[key]);
            if (!tag) return d;

            d[key] = { Name: tag[key]?.Name, val: 0, readable: 0 };
            return d;
          }

          d[key] = { val: 0, readable: 0 };
        }
      }

      return d;
    });

    return (
      <ResponsiveContainer>
        <LineChart data={finalData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={xAxisField}
            fontSize={14}
            tickLine={false}
            axisLine={true}
            fontWeight={500}
            tickFormatter={(value) => value || `No ${groupByFieldName}`}
          />
          <YAxis
            tickLine={false}
            fontSize={14}
            axisLine={true}
            fontWeight={500}
          />
          <Tooltip
            content={(props) => (
              <GroupedChartTooltip
                {...props}
                xAxisField={xAxisField}
                xAxisFieldName={xAxisFieldName}
                colorKey={"stroke"}
                groupByFieldType={groupByFieldType}
              />
            )}
          />
          <Legend
            formatter={(value) => {
              if (groupByFieldType === "Tags") {
                if (groupByFieldType === "Tags") {
                  const match = finalData.find((d) => !!d[value]);
                  value = match[value]?.Name ?? value;
                }
              }

              return (
                <span className="text-sm font-weight-500 pr-1 text-medium">
                  {value}
                </span>
              );
            }}
          />
          {uniqueKeysArray.map((key: string, i: number) => {
            return (
              <Line
                key={key}
                connectNulls
                type="monotone"
                name={key}
                dataKey={`${key}.val`}
                stroke={colors[i]}
                activeDot={{ r: 8 }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    );
  }, [data, keys, groupByField, loading, xAxisField, section]);

  if (error) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (loading) {
    return <WidgetLoading />;
  }

  return MyChart;
};

export default GroupedLineChartWidget;
